import { msg } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";

import type { Screen } from "../../state/screen";

export type TutorialPhase = "dashboard" | "contract" | "logs" | "mail" | "report" | "done";

export type PointerSide = "top" | "bottom" | "left" | "right";

export type TutorialAdvance =
  | { kind: "next" }
  | { kind: "navigate"; to: Screen }
  | { kind: "skillUpgraded" }
  | { kind: "toolPurchased" };

/** Shown instead of the main coachmark once the task is submitted and the continue control exists. */
export interface TutorialDone {
  targetId: string;
  title: MessageDescriptor;
  body: MessageDescriptor;
}

export interface TutorialStep {
  phase: TutorialPhase;
  screen: Screen;
  targetId: string;
  title: MessageDescriptor;
  body: MessageDescriptor;
  advance: TutorialAdvance;
  pointerSide?: PointerSide;
  final?: boolean;
  done?: TutorialDone;
}

/**
 * Guided onboarding spine (TUTORIAL_FLOW.md): ten steps through contract
 * greenshop_01: logs, inbox, report - from the dashboard to the results
 * screen. Action steps auto-advance on a store-observable transition (screen
 * change, skill or tool gain); the final info step advances via the bubble
 * button. Each task is a single coachmark (mark + submit) because in-game
 * selection is local state. The skills and tools screens are introduced by
 * one-off hints instead (tutorialHints.ts).
 */
export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    phase: "dashboard",
    screen: "dashboard",
    targetId: "recommended-contract",
    title: msg`Это твоя консоль`,
    body: msg`Сверху - деньги ($300), опыт и уровень. Первая работа уже ждёт: открой рекомендованный контракт.`,
    advance: { kind: "navigate", to: "contract_details" },
    pointerSide: "right",
  },
  {
    phase: "contract",
    screen: "contract_details",
    targetId: "take-contract",
    title: msg`Брифинг клиента`,
    body: msg`Справа - награда за контракт и доверие клиента. Возьми контракт, чтобы начать.`,
    advance: { kind: "navigate", to: "workspace" },
    pointerSide: "left",
  },
  {
    phase: "contract",
    screen: "workspace",
    targetId: "task-action",
    title: msg`Кейс-файл клиента`,
    body: msg`Три задачи по порядку - пройдём каждую. Жми "Начать" на первой.`,
    advance: { kind: "navigate", to: "log_analysis" },
    pointerSide: "left",
  },
  {
    phase: "logs",
    screen: "log_analysis",
    targetId: "suspicious-row",
    title: msg`Отметь подозрительные`,
    body: msg`Ночью под учёткой подрядчика меняли роли и выгружали заказы - так днём не работают. Подозрительных строк может быть несколько: отметь все подсвеченные и нажми "Проверить".`,
    advance: { kind: "navigate", to: "workspace" },
    pointerSide: "bottom",
    done: {
      targetId: "task-continue",
      title: msg`Логи проверены`,
      body: msg`Смотри разбор по строкам: что верно, что пропущено. Готов - жми "Продолжить".`,
    },
  },
  {
    phase: "mail",
    screen: "workspace",
    targetId: "task-action",
    title: msg`Следующая задача`,
    body: msg`Логи разобрал, оценка уже в кейсе. Дальше - входящие клиента. Жми "Начать".`,
    advance: { kind: "navigate", to: "phishing" },
    pointerSide: "left",
  },
  {
    phase: "mail",
    screen: "phishing",
    targetId: "phishing-row",
    title: msg`Найди фишинг`,
    body: msg`Это почта клиента. Кто-то просит срочно подтвердить реквизиты по ссылке - так платёжный шлюз не пишет. Отметь все подозрительные письма и нажми "Проверить".`,
    advance: { kind: "navigate", to: "workspace" },
    pointerSide: "bottom",
    done: {
      targetId: "task-continue",
      title: msg`Почта проверена`,
      body: msg`Смотри разбор по письмам: что верно, что пропущено. Готов - жми "Продолжить".`,
    },
  },
  {
    phase: "report",
    screen: "workspace",
    targetId: "task-action",
    title: msg`Финал контракта`,
    body: msg`Осталась сборка отчёта. Жми "Начать".`,
    advance: { kind: "navigate", to: "report_builder" },
    pointerSide: "left",
  },
  {
    phase: "report",
    screen: "report_builder",
    targetId: "findings",
    title: msg`Собери отчёт`,
    body: msg`Выбери рекомендации в рамках бюджета - полоска показывает, сколько потрачено. Потом отправь отчёт.`,
    advance: { kind: "navigate", to: "workspace" },
    pointerSide: "top",
    done: {
      targetId: "task-continue",
      title: msg`Отчёт отправлен`,
      body: msg`Счёт учитывает выводы, меры, порядок и бюджет. Готов - жми "Продолжить".`,
    },
  },
  {
    phase: "report",
    screen: "workspace",
    targetId: "complete-contract",
    title: msg`Закрой контракт`,
    body: msg`Все три задачи готовы. Заверши контракт, чтобы увидеть результат.`,
    advance: { kind: "navigate", to: "results" },
    pointerSide: "top",
  },
  {
    phase: "done",
    screen: "results",
    targetId: "grade-seal",
    title: msg`Готово!`,
    body: msg`Первый контракт закрыт: деньги, опыт и репутация уже начислены. Следующий ждёт на главной.`,
    advance: { kind: "next" },
    pointerSide: "bottom",
    final: true,
  },
] as const;

export const TUTORIAL_TOTAL = TUTORIAL_STEPS.length;

/** Index of the first step where the tutorial may be skipped (after the first task is submitted). */
export const SKIP_AVAILABLE_FROM_INDEX = 4;

export function isSkipAvailable(stepIndex: number): boolean {
  return stepIndex >= SKIP_AVAILABLE_FROM_INDEX;
}
